import { useEmailMessageSearch } from '@Notifications/api/EmailMessageApi';
import { usePermissions } from '@Pages/pageRuntime/hooks/usePermissions';
import { toApiError } from '@Pages/pageRuntime/sdk/apiErrors';
import type {
  SdkApiHandle,
  SdkEmailMessages,
  SdkEmailMessagesOptions
} from '@Pages/pageRuntime/sdk/types';
import { useQueryClient } from '@tanstack/react-query';

import { SystemPermission } from '@/types/enums';

import {
  buildSearchRequest,
  scopeProblem,
  scopeRecords,
  searchFromFilters
} from './emailMessageSearch';
import { usePollCountdown, usePollRefetchInterval } from './pollInterval';

export function useEmailMessages(options: SdkEmailMessagesOptions): SdkApiHandle<SdkEmailMessages> {
  const queryClient = useQueryClient();
  const { hasPermission } = usePermissions();
  const canViewEmailHistory = hasPermission(SystemPermission.ManageNotifications);
  const records = scopeRecords(options);
  const problem = scopeProblem(options, records, canViewEmailHistory);
  const enabled =
    (options.enabled ?? true) &&
    problem === null &&
    (options.workspace === true || records.length > 0);
  const applied = searchFromFilters(options.filters);
  applied.page = options.page ?? 1;
  const request = enabled
    ? {
        ...buildSearchRequest(applied, records, options.perPage ?? 50, canViewEmailHistory),
        sort: options.sort
      }
    : null;
  const pollInterval =
    options.pollInterval ?? (options.workspace || options.records ? false : 30_000);
  const poll = usePollRefetchInterval<
    Diagonal.Notifications.Data.IndexEmailMessagesData,
    SdkEmailMessages | undefined
  >(pollInterval, (data) => data);
  const query = useEmailMessageSearch(request, {
    refetchInterval: poll.refetchInterval,
    keepPreviousData: false
  });
  const refreshIn = usePollCountdown(poll, query, enabled);

  return {
    data: query.data,
    isLoading: query.isLoading,
    isError: problem !== null || query.isError,
    error:
      problem !== null
        ? toApiError(new Error(problem))
        : query.error
          ? toApiError(query.error)
          : null,
    refreshIn,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: ['email-messages', 'search', request],
        exact: true
      })
  };
}
